
import { useEffect, useCallback, useMemo, useState, useRef, useContext } from "react";
import { createRoot } from 'react-dom/client';
import { AgGridReact } from '@ag-grid-community/react';
import { InfiniteRowModelModule } from '@ag-grid-community/infinite-row-model';
import '@ag-grid-community/styles/ag-grid.css';
import "@ag-grid-community/styles/ag-theme-quartz.css";
import "./customStyles.css";
//import "@ag-grid-community/styles/ag-theme-balham.css";

import { ModuleRegistry } from '@ag-grid-community/core';
import { DDD } from "../../lib/ddd/ddd";
import { ReactComponent as EditBtn } from "../../assets/enter.svg";
import { ReactComponent as CopyBtn } from "../../assets/copy.svg";
import classes from "./CustomersGrid.module.scss";
import { NotificationManager} from 'react-notifications';
import useEffectIgnoreFirst from "../../hooks/useEffectIgnoreFirst";
import { useTranslation } from 'react-i18next';
import { OrgIdContext } from "../../store/orgId_context";
import HelpIcon from '@mui/icons-material/Help';
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import CallSplitIcon from '@mui/icons-material/CallSplit';
import MergeIcon from '@mui/icons-material/Merge';
import JoinFullIcon from '@mui/icons-material/JoinFull';
import JoinInnerIcon from '@mui/icons-material/JoinInner';
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

ModuleRegistry.registerModules([InfiniteRowModelModule]);

const apiRequestAddress = process.env.REACT_APP_API_ENDPOINT || window.location.origin + "/json.rpc";
const ddd = new DDD(apiRequestAddress);

const CustomersGrid = ({openClientInfoHandler, search, changeClientType, reloadNum, filter}) =>{
    const table =useRef();
    const [requestParams, setRequestParams] = useState();
    const {sessionInfo, mainOrgId} = useContext(OrgIdContext)
    const { t } = useTranslation();

    const [columnDefs, setColumnDefs] = useState([
        {
            field : "__open",
            headerName : t("Go to"),
            sortable : false,
            maxWidth:100,
            flex:1,
            cellRenderer : (params) => {return (<>
                <OverlayTrigger placement="top" overlay={<Tooltip>{t("Go to the customer's account")}</Tooltip>}>
                    <EditBtn className={classes["conn-key"]} onClick={() =>openClientInfoHandler(params.data.CustomerId,params.data.ConnectionKey)}/>
                </OverlayTrigger>
                {mainOrgId === params?.data?.CustomerId && 
                    <OverlayTrigger placement="top" overlay={<Tooltip>{t("Should you also wish to issue/recieve invoices under your own name, this company was pre-created for you when you registered.")}</Tooltip>}>
                        <HelpIcon style={{marginLeft:"0.5rem", fontSize:"1rem", color:"var(--main-blue)"}}/>
                    </OverlayTrigger>}
                </>)}
        },
        {
            field : "ClientType",
            headerName : t("Type"),
            maxWidth:100,
            flex:1,
            cellStyle : {
                textAlign :"center"
            },
            cellRenderer : (params) => {return (<>
                
                {params?.data?.ClientType === "Company" && 
                    <OverlayTrigger placement="top" overlay={<Tooltip>{t("This customer has a bussiness relationship directly with us. Click to change bussiness relationship.")}</Tooltip>}>
                        <JoinInnerIcon className={classes["relationship"]} onClick={() => changeClientType(params?.data)}/>
                    </OverlayTrigger>
                }
                {params?.data?.ClientType === null && 
                    <OverlayTrigger placement="top" overlay={<Tooltip>{t("This customer has a bussiness relationship with you and its invoices count fall under your package. Click to change bussiness relationship.")}</Tooltip>}>
                        <JoinFullIcon className={classes["relationship"]} onClick={() => changeClientType(params?.data)}/>
                    </OverlayTrigger>
                }
                
            </>)}
        },
        {
            field : "Logo",
            headerName : t("Logo"),
            sortable : false,
            maxWidth:90,
            flex:1,
            cellStyle : {
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center"
            },
            cellRenderer : (params) => {
                if(!params?.data?.Logo) return;
                return (
                <img alt="Logo" style={{ width: "2rem", height:"2rem", borderRadius:"20%" }} src={process.env.REACT_APP_ORIGIN ?  process.env.REACT_APP_ORIGIN+"/"+params.data.Logo : params.data.Logo}/>/* params.data.Logo */
            )}
        },
        {
            field : "CountryCode",
            headerName : t("Country"),
            minWidth: 100,
            cellStyle : {
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            },
            cellRenderer: (params) => {
                if (!params.value) return "";
                const countryCode = params.value.toLowerCase(); // API needs lowercase (e.g., "kw", "de")
                const flagUrl = `https://flagcdn.com/16x12/${countryCode}.png`; // 16x12px flag image
                return <>
                    <img src={flagUrl} alt={params.value} style={{ width: "16px", height: "12px" }} onerror="this.style.display='none'" />
                    <span style={{ marginLeft: "5px" }}>{params.value}</span>
                </>;
            }
        },
        {
            field : "Organisation",
            headerName : t("Organisation"),
            flex:1,
        },
        {
            field : "ConnectionKey",
            headerName : t("Connection Key"),
            flex: 3,
            minWidth:350,
            cellRenderer : (params) =>{
                return(
                    <>
                    <span className={classes["conn-key"]}  onClick={() => toClipboard(params.value)}>{params.value} <CopyBtn height="15" fill="#00436D" /></span>
                    </>
                )
            }
        },
        {
            field : "eMail",
            headerName : t("Email")
        },
        {
            field : "Telephone",
            headerName : t("Telephone")
        },
        {
            field : "TaxNum",
            headerName : t("Tax number"),
            minWidth:150,
        },
        {
            field : "ThisMonth",
            headerName : t("This Month"),
            type: 'currency',
            headerComponent: CustomHeader,
            maxWidth:150,
            cellRenderer : (params) => {return params.value||0},
        },
        {
            field : "LastMonth",
            headerName : t("Last Month"),
            type: 'currency',
            headerComponent: CustomHeader,
            maxWidth:150,
            cellRenderer : (params) => {return params.value||0},
        },
        {
            field : "All",
            headerName : t("All"),
            maxWidth:150,
            headerComponent: CustomHeader,
            cellRenderer : (params) => {return ((params?.data?.ThisMonth||0)+(params?.data?.LastMonth||0)+(params?.data?.All||0))||0},
        },
    ]);

    const toClipboard = (val) =>{
        navigator.clipboard.writeText(val);
        NotificationManager.success(t("Copied"), "");
    }
    const defaultColDef = useMemo(() => {
        return {
            flex: 1,
            minWidth: 200,
            sortable: true,
        }
    }, []);

    let _search = search;
    const onGridReady = useCallback((params) => {
        const dataSource = {
            rowCount: undefined,
            getRows: (reqParams) => {
                setRequestParams(reqParams);
                loadEntityData(reqParams).then((res) =>{
                    reqParams.successCallback(res?.result?.Result, res?.result?.Count);
                });
            }
        }
        params.api.setGridOption('datasource', dataSource);
    });

    const loadEntityData = ({startRow,endRow, search,filter, sortModel}) => {
        let args = {
            "IncludeCount" :true,
            "Skip" : startRow,
            "Take" : endRow-startRow,
            "Filter" : getFilterFromSearch(search, filter),
            "OrderBy" : getOrderByFromArray(sortModel),
        };
        return ddd.Rpc_ExecuteExtension("EUeInvoices.GetTCustomers", args)
    }

    const getOrderByFromArray = (arr) => {
        if(!arr?.length) return null;

        let order = "";
        arr.forEach(item => order+= item.colId + " " + item.sort+", ");
        order = order.replace(/, +$/, "");
        return order;
    }
    const getFilterFromSearch = (globalSearch, filter) =>{
        let ff="";
        if((!globalSearch || globalSearch.trim() ==="") && !filter) return null;

        if(globalSearch && globalSearch.trim() !=="")
            ff=`Organisation contains "${globalSearch}" OR ConnectionKey contains "${globalSearch}" OR eMail contains "${globalSearch}" OR TaxNum contains "${globalSearch}"`;
        if(filter){
            if(ff) ff= `(${ff}) AND `;
            ff += filter;
        }
        return ff==="" ? null : ff;
    }

    useEffectIgnoreFirst(() =>{console.log(reloadNum);
        const dataSource = {
            rowCount: undefined,
            getRows: (reqParams) => {
                reqParams.search = search;
                reqParams.filter = filter;
                setRequestParams(reqParams);
                loadEntityData(reqParams).then((res) =>{
                    console.log(res);
                    reqParams.successCallback(res?.result?.Result, res?.result?.Count);
                });
            }
        }
        table?.current?.api?.setGridOption('datasource', dataSource);
        //table?.current?.api?.refreshServerSide({ route: undefined, purge: true });
    }, [search, filter, reloadNum]);

    return(
        <div style={{ height: '100%', width: '100%' }} className={"ag-theme-quartz"}>
            <AgGridReact
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowBuffer={0}
                rowSelection={'multiple'}
                rowModelType={'infinite'}
                cacheBlockSize={100}
                cacheOverflowSize={2}
                maxConcurrentDatasourceRequests={1}
                infiniteInitialRowCount={1000}
                maxBlocksInCache={10}
                onGridReady={onGridReady}
                ref = {table}
            />
        </div>
    )
}


export default CustomersGrid;

const CustomHeader = (props) => {
  const { displayName } = props; // comes from headerName
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <span>{displayName}</span>
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip>
            {t("This number is recalculated once per day")}
          </Tooltip>
        }
      >
        <HelpOutlineIcon
          style={{ marginLeft: "0.5rem", fontSize: "1rem", cursor: "pointer", color: "var(--main-blue)" }}
        />
      </OverlayTrigger>
    </div>
  );
};
